import { Category, Listing, OfferType } from '@smg-real-estate/hg-rets-schema';

import { isLocationValidForUbs } from '../functions/helpers';

// Property types that are completely unsupported by UBS Immocheck
const UNSUPPORTED_PROPERTY_TYPES: Category[] = [
  // Commercial and residential building
  Category.ResidentialCommercialBuilding,

  // Parking space, garage
  Category.ParkingSpace,
  Category.Garage,
  Category.SingleGarage,
  Category.DoubleGarage,
  Category.UndergroundSlot,
  Category.OpenSlot,
  Category.CoveredSlot,
  Category.CarPark,

  // Commercial, Office, Storage room
  Category.Commercial,
  Category.Office,
  Category.StorageRoom,
  Category.Shop,
  Category.Workshop,
  Category.Atelier,
  Category.IndustrialObject,
  Category.Practice,
  Category.Factory,
  Category.DepartmentStore,
  Category.Warehouse,
];

// Property types that are supported but have value limits
const SUPPORTED_PROPERTY_TYPES_WITH_LIMITS: Category[] = [
  // Apartment
  Category.Apartment,
  Category.AtticFlat,
  Category.Studio,
  Category.SingleRoom,
  Category.FurnishedFlat,
  Category.Loft,
  Category.Attic,
  Category.TerraceFlat,
  Category.GrannyFlat,
  Category.RoofFlat,
  Category.Duplex,
  Category.SharedApartment,

  // House, chalet, rustico
  Category.House,
  Category.Chalet,
  Category.RusticHouse,
  Category.Rustico,
  Category.SingleHouse,
  Category.Villa,
  Category.Castle,
  Category.FarmHouse,
  Category.RowHouse,
  Category.TerraceHouse,
  Category.BifamiliarHouse,

  // Multi-family house
  Category.MultipleDwelling,
];

// Maximum limits for supported property types
const MAX_LIVING_SPACE = 300; // m²
const MAX_NUMBER_OF_ROOMS = 9;

// Lot size limits for house types only
const MIN_LOT_SIZE = 100; // m²
const MAX_LOT_SIZE = 2000; // m²

// House types that should have lot size validation
const HOUSE_TYPES_WITH_LOT_SIZE: Category[] = [
  Category.House,
  Category.Chalet,
  Category.RusticHouse,
  Category.Rustico,
  Category.SingleHouse,
  Category.Villa,
  Category.Castle,
  Category.FarmHouse,
  Category.RowHouse,
  Category.TerraceHouse,
  Category.BifamiliarHouse,
  Category.MultipleDwelling,
];

/**
 * Checks if the listing contains any unsupported property types
 */
const hasUnsupportedPropertyType = (listing: Listing): boolean => {
  if (!listing.categories || listing.categories.length === 0) {
    return false;
  }

  return listing.categories.some((category) =>
    UNSUPPORTED_PROPERTY_TYPES.includes(category as Category),
  );
};

/**
 * Checks if the listing has supported property types but exceeds value limits
 */
const exceedsValueLimits = (listing: Listing): boolean => {
  if (!listing.categories || listing.categories.length === 0) {
    return false;
  }

  // Check if listing has any supported property types
  const hasSupportedType = listing.categories.some((category) =>
    SUPPORTED_PROPERTY_TYPES_WITH_LIMITS.includes(category as Category),
  );

  if (!hasSupportedType) {
    return false;
  }

  const livingSpace = listing.characteristics?.livingSpace;
  const numberOfRooms = listing.characteristics?.numberOfRooms;
  const lotSize = listing.characteristics?.lotSize;

  // Hide if living space > 300 m² OR number of rooms > 9
  if (
    (livingSpace !== undefined && livingSpace > MAX_LIVING_SPACE) ||
    (numberOfRooms !== undefined && numberOfRooms > MAX_NUMBER_OF_ROOMS)
  ) {
    return true;
  }

  // Additional validation for house types: lot size must be between 100-2000 m²
  const isHouseType = listing.categories.some((category) =>
    HOUSE_TYPES_WITH_LOT_SIZE.includes(category as Category),
  );

  if (isHouseType && lotSize !== undefined) {
    return lotSize < MIN_LOT_SIZE || lotSize > MAX_LOT_SIZE;
  }

  return false;
};

/**
 * Checks if the construction year is valid for UBS ImmoCheck
 * - If present, must be between 1900 and current year (inclusive)
 * - If not present, allow ImmoCheck flow (user can enter manually)
 */
const hasInvalidConstructionYear = (listing: Listing): boolean => {
  const yearBuilt = listing.characteristics?.yearBuilt;

  // If yearBuilt is not present, allow the flow (user can enter manually in ImmoCheck)
  if (yearBuilt === undefined || yearBuilt === null) {
    return false;
  }

  const currentYear = new Date().getFullYear();

  // Check if year is outside the valid range (1900 to current year inclusive)
  return yearBuilt < 1900 || yearBuilt > currentYear;
};

export const isListingValidForUbsImmoCheck = (listing: Listing, locale: string): boolean => {
  return (
    listing.offerType === OfferType.Buy &&
    isLocationValidForUbs(listing) &&
    !hasUnsupportedPropertyType(listing) &&
    !exceedsValueLimits(listing) &&
    !hasInvalidConstructionYear(listing)
  );
};

export const getActionLink = (locale: string, listingId: string | undefined | null): string => {
  // Return an empty anchor if the id is undefined and if locale is invalid
  if (!listingId) {
    return '#';
  }

  const baseUrl = import.meta.env.VUE_APP_BASE_URL as string;
  return `${baseUrl}/${locale}/price-check/${listingId}`;
};
